<template>
  <div id="app-2021-11-inkomstgrupper_fi">
    <div class="intro">
      <div class="content-box">
        <h1>Paljonko tienaat verrattuna muihin? Katso, mikä on oma vaikutuksesi valtion veropottiin ja mistä koko kakku muodostuu</h1>
        <p>
          Joka vuosi veropäivänä suurituloisimpien tulot ja verot nousevat valtakunnan ykkösuutiseksi. Valtio kerää vuosittain lähes {{ formatNo(allTaxes.total.sum, 1, true) }} miljardia euroa erilaisia veroja, joista kolmasosa kertyy tuloista.
        </p>
        <p>
          Median raportointi keskittyy useimmiten pieneen osaan kaikista 5,1 miljoonasta veronmaksajasta. 
          Tiedätkö, paljonko sinä tienaat suhteessa muihin ja mistä veropotti kokonaisuutena muodostuu?
        </p>
        <p>
          Visualisoinnit on tehty vuoden 2019 verotilastojen pohjalta.
        </p>
        <p>Aloita täyttämällä kuukausi- tai vuositulosi. Jos et halua käyttää omia tulojasi, voit käyttää koneeseen valmiiksi syötettyä mediaanituloa.</p>
        <label for="income-input">
          Kuukausipalkka (ilman lomarahoja):
        </label>
        <input
          id="income-input"
          v-model.number="monthly_income"
          @input="updateIncome"
          class="yddviz-input"
          type="number"
        />
        <label for="month-income-input">
          Vuositulo (bruttona, sisältäen lomarahat):
        </label>
        <input
          id="month-income-input"
          v-model.number="income"
          @input="updateMonthly"
          class="yddviz-input"
          type="number"
        />
        <p>
          <em>
            Bruttotuloilla tarkoitetaan tuloja ennen veroja ja vähennyksiä.
            Tuloja ovat esimerkiksi palkka (sisältäen lomarahat), tuet, eläke ja pääomatulot. 
          </em>
        </p>
      </div>
    </div>
    <div class="fyrfalt-scroll_wrapper" v-if="incomeGroup !== null">
      <div class="fyrfalt-background">
        <SvgContent
          :colors="colors"
          :allTaxes="allTaxes"
          :inkomstgrupper="inkomstgrupper"
          :currentView="currentView"
          :incomeGroup="incomeGroup"
          :showTaxes="showTaxes"
          :showAllTaxes="showAllTaxes"
          :showEvenMoreTaxes="showEvenMoreTaxes"
          :showCategories="showCategories"
          :coloredDots="coloredDots"
        />
      </div>
      <div class="fyrfalt-content">
        <div
          class="content-box first"
          data-trigger="intro"
          v-waypoint="{ active: true, callback: onWaypoint }"
        >
          <p>
            Nämä {{ formatNo(dotsCount) }} palloa edustavat kaikkia Suomessa vuonna 2019 veroja maksaneita.
            Jokainen pallo vastaa noin
            {{ formatNo(100 * Math.round(popCount / dotsCount / 100)) }}
            veronmaksajaa.
          </p>

          <p>
            Näin <strong>vuositulot</strong> jakautuivat vuonna 2019.
            Selaa alaspäin nähdäksesi lisää.
          </p>
        </div>
        <div
          class="content-box"
          data-trigger="showYour"
          v-waypoint="{ active: true, callback: onWaypoint }"
        >
          <p>
            Sinä kuulut tuloluokkaan, joka ansaitsi 
            <template v-if="incomeGroup === 0">
              korkeintaan {{ formatNo(inkomstgrupper[incomeGroup].higher) }} €
            </template>
            <template v-else-if="incomeGroup === inkomstgrupper.length - 1">
              enemmän kuin {{ formatNo(inkomstgrupper[incomeGroup].lower) }} €
            </template>
            <template v-else>
              {{ inkomstgrupper[incomeGroup].name }}
            </template>
            vuodessa.
            Teitä on yhteensä {{ formatNo(inkomstgrupper[incomeGroup].total) }} henkilöä,
            ja muodostatte {{ formatNo((100 * inkomstgrupper[incomeGroup].total) / popCount, 1) }} %
            veronmaksajista.
          </p>
        </div>
        <div
          class="content-box"
          data-trigger="showLess"
          v-waypoint="{ active: true, callback: onWaypoint }"
          v-if="lowerIncome"
        >
          <p>{{ lowerIncome }} oli matalammat tulot kuin sinun ryhmälläsi.</p>
        </div>
        <div
          class="content-box"
          data-trigger="showMore"
          v-waypoint="{ active: true, callback: onWaypoint }"
          v-if="higherIncome"
        >
          <p>{{ higherIncome }} oli korkeammat tulot kuin sinun ryhmälläsi.</p>
        </div>
        <div
          class="content-box"
          data-trigger="showHighest"
          v-waypoint="{ active: true, callback: onWaypoint }"
          v-if="incomeGroup < inkomstgrupper.length - 1"
        >
          <p>
           Suurituloisimpaan luokkaan kuuluvat ansaitsevat enemmän kuin 300 000 euroa vuodessa.
            Heitä oli ainoastaan 
            {{ formatNo(pops[pops.length - 1]) }} henkilöä, 
            runsas promille veronmaksajista.
          </p>
        </div>
        <div
          class="content-box"
          data-trigger="showGenders"
          v-waypoint="{ active: true, callback: onWaypoint }"
        >
          <p>
            {{ inkomstgrupper[incomeGroup].men_share }} % ryhmästäsi on
            <strong :style="`color: ${colors.man}`">miehiä</strong> ja
            {{ inkomstgrupper[incomeGroup].women_share }} % on
            <strong :style="`color: ${colors.woman}`">naisia</strong>.
          </p>
        </div>
        <div
          class="content-box"
          data-trigger="showAllGenders"
          v-waypoint="{ active: true, callback: onWaypoint }"
        >
          <p>
            Ylipäätään korkeammat tulot ovat yleisempiä <strong :style="`color: ${colors.man}`">miesten</strong> kuin <strong :style="`color: ${colors.woman}`">naisten</strong>
            keskuudessa.
          </p>
          <p>Katsotaan seuraavaksi tarkemmin tuloluokkasi maksamia veroja.</p>
        </div>
        <div
          class="content-box"
          data-trigger="showTaxes"
          v-waypoint="{ active: true, callback: onWaypoint }"
        >
          <p>
            Yhteensä maksoitte veroja
            {{ formatNo(inkomstgrupper[incomeGroup].taxes, 1, true) }} miljardia euroa
             – se on keskimäärin
            {{ formatNo(inkomstgrupper[incomeGroup].taxes_per_person) }} euroa
            henkilöä kohti. Keskimääräinen veroprosentti ryhmässäsi oli
            {{ formatNo((100 * inkomstgrupper[incomeGroup].taxes) / inkomstgrupper[incomeGroup].income, 1) }} %.
          </p>
        </div>
        <div
          class="content-box"
          data-trigger="showAllTaxes"
          v-waypoint="{ active: true, callback: onWaypoint }"
        >
          <p>
            Veronne muodostivat {{ formatNo(percentTaxes, 1) }} %  
           kotitalouksien maksamista tuloveroista.
          </p>
        </div>
        <div
          class="content-box"
          data-trigger="showEvenMoreTaxes"
          v-waypoint="{ active: true, callback: onWaypoint }"
        >
          <p>
            Vuonna 2019 yhteiskunta keräsi yhteensä {{ formatNo(allTaxes.total.sum, 1, true) }} miljardia euroa erilaisia veroja ja maksuja.
          </p>
          <p>Yli kolmannes summasta tuli erilaisista tuloveroista. Suurin osuus on peräisin kotitalouksien tuloista.</p>
        </div>
        <div
          class="content-box"
          data-trigger="showCompanies"
          v-waypoint="{ active: true, callback: onWaypoint }"
        >
          <p>
            Myös yritykset ja organisaatiot maksavat tuloistaan veroa. Niiltä kerätty <em>yhteisövero</em> toi {{ formatNo(allTaxes.org.sum,1,true) }} miljardia euroa valtiolle ja kunnille.
          </p>
        </div>
        <div
          class="content-box"
          data-trigger="showVat"
          v-waypoint="{ active: true, callback: onWaypoint }"
        >
          <p>
            Tavaroita ja palveluitakin verotetaan. Lähes aina ostaessasi jotakin tai maksaessasi palvelusta summaan lisätään 10–24 prosentin <em>arvonlisävero</em>. 
            Arvonlisäverot toivat {{ formatNo(allTaxes.vat.sum,1,true) }} miljardia euroa veropottiin.
          </p>
        </div>
        <div
          class="content-box"
          data-trigger="showExcise"
          v-waypoint="{ active: true, callback: onWaypoint }"
        >
          <p>
            Tietyistä asioista maksamme lisäksi erillistä <em>valmisteveroa</em>. Suurimmat valmisteverot ovat sähkön ja polttoaineiden energiaverot ja uusista autoista maksettava autovero. 
            Myös alkoholia, tupakkaa ja virvoitusjuomia verotetaan erikseen.
          </p>
          <p>Vuonna 2019 valmisteverot muodostivat {{ formatNo(100*allTaxes.excise.share) }} % kaikista Suomen veroista.</p>
        </div>
        <div
          class="content-box"
          data-trigger="showGift"
          v-waypoint="{ active: true, callback: onWaypoint }"
        >
          <p>Veroa maksetaan silloinkin, kun saamme tai perimme jotakin. <em>Perintö- ja lahjaverot</em> muodostivat {{ formatNo(100*allTaxes.gift.share) }} % Suomen verotuloista.</p>
        </div>
        <div
          class="content-box"
          data-trigger="showSS"
          v-waypoint="{ active: true, callback: onWaypoint }"
        >
        <p>Varsinaisten verojen ohella työntekijät ja työnantajat maksoivat {{ formatNo(allTaxes.ss.sum,2,true) }} miljardia euroa erilaisia sosiaaliturvamaksuja. Ne muodostavat {{ formatNo(100*allTaxes.ss.share) }} % koko kakusta.</p>
        </div>
      </div>
    </div>
    <div class="end" v-if="incomeGroup !== null">
      <div>
        <div class="content-box">
          <h2>Kiitos veroistasi!</h2>
          <p class="credits">
            Tekijät: Linus Lång, Petter West, Malin Ekholm, Kaisa Uusitalo, Joel Kanerva<br />
            Lähde: Verovirasto.<br />
            Julkaistu ensimmäisen kerran 6.11.2019, päivitetty 4.11.2020 ja 9.11.2021.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const d3 = Object.assign(
  {},
  require('d3-array'),
);

import inkomstgrupper from './inkomstgrupper.json';
import allTaxes from './skatter.json';

import SvgContent from './components/SvgContent.vue';

const pops = inkomstgrupper.reduce(function (acc, cv) {
  acc.push(cv.total);
  return acc;
}, []);

const dotsCount = inkomstgrupper.reduce((acc, cv) => cv["total_dots"] + acc, 0);
const popCount = inkomstgrupper.reduce((acc, cv) => cv["total"] + acc, 0);

export default {
  name: "app",
  components: {
    SvgContent,
  },
  data() {
    return {
      mounted: false,
      inkomstgrupper,
      income: 30550,
      monthly_income: null,
      currentView: "all",
      pops: pops,
      dotsCount: dotsCount,
      popCount: popCount,
      allTaxes: allTaxes,
      showTaxes: false,
      showAllTaxes: false,
      showEvenMoreTaxes: false,
      showCategories: 1,
      coloredDots: null,
      darkMode: false,
    };
  },
  computed: {
    colors() {
      return {
        selected: '#00B4C8',
        gray: '#B4B6B7',
        deselected: this.darkMode ? '#4A4F54' : '#D8D9DB',
        man: '#2875cf',
        woman: '#ff8d34',
        background: this.darkMode ? '#1b1e20' : '#fff',
      }
    
    },
    percentTaxes() {
      if (this.incomeGroup !== null) {
        return (this.inkomstgrupper[this.incomeGroup].taxes / this.allTaxes.income.sum) * 100;
      }
      return null;
    },
    incomeGroup() {
      const income = this.income;
      if (income) {
        if (income < 100000) {
          return Math.floor(income / 10000);
        } else if (income < 150000) {
          return 10;
        } else if (income < 300000) {
          return 11;
        } else return 12;
      } else return null;
    },
    lowerIncome() {
      if (this.incomeGroup) {
        const taxPayers = d3.sum(this.pops.slice(0, this.incomeGroup));
        const share = (100 * d3.sum(this.pops.slice(0, this.incomeGroup))) / popCount;

        return `${this.formatNo(taxPayers)} veronmaksajalla, eli ${this.formatNo(share, 1)} %:lla`;
      }
      return null;
    },
    higherIncome() {
      if (this.incomeGroup === this.inkomstgrupper.length - 1) {
        return null;
      }

      const taxPayers = d3.sum(this.pops.slice(this.incomeGroup + 1, this.inkomstgrupper.length));
      const share = (100 * d3.sum(this.pops.slice(this.incomeGroup + 1, this.inkomstgrupper.length))) / popCount;

      return `${this.formatNo(taxPayers)} veronmaksajalla, eli ${this.formatNo(share, 1)} %:lla`;
    },
  },
  mounted() {
    this.updateMonthly();
    this.darkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
  },
  methods: {
    formatNo(value, digits = 0, billions = false) {
      if (billions) {
        // divide by 1 billion
        value = value / 1000000000;
      }

      return value.toLocaleString('sv-FI', {
        maximumFractionDigits: digits,
        useGrouping: true,
      })
    },
    updateIncome() {
      this.income = this.monthly_income * 12.5;
    },
    updateMonthly() {
      this.monthly_income = this.income / 12.5;
    },
    onWaypoint({ going, el }) {
      if (going === this.$waypointMap.GOING_IN) {
        switch (el.dataset.trigger) {
          case "intro":
            this.reset();
            break;
          case "showYour":
            this.coloredDots = "same";
            break;
          case "showLess":
            this.coloredDots = "less";
            break;
          case "showMore":
            this.coloredDots = "more";
            break;
          case "showHighest":
            this.coloredDots = "highest";
            break;
          case "hideOther":
            this.coloredDots = "same";
            break;
          case "showGenders":
            this.coloredDots = "genders";
            break;
          case "showAllGenders":
            this.coloredDots = "all_genders";
            this.showTaxes = false;
            break;
          case "showTaxes":
            this.showTaxes = true;
            this.showAllTaxes = false;
            this.showEvenMoreTaxes = false;
            break;
          case "showAllTaxes":
            this.showTaxes = true;
            this.showAllTaxes = true;
            this.showEvenMoreTaxes = false;
            break;
          case "showEvenMoreTaxes":
            this.showTaxes = true;
            this.showEvenMoreTaxes = true;
            this.showCategories = 1;
            break;
          case "showCompanies":
            this.showTaxes = true;
            this.showEvenMoreTaxes = true;
            this.showCategories = 2;
            this.showAllTaxes = false;
            break;
          case "showVat":
            this.showTaxes = true;
            this.showEvenMoreTaxes = true;
            this.showCategories = 3;
            break;
          case "showExcise":
            this.showTaxes = true;
            this.showEvenMoreTaxes = true;
            this.showCategories = 4;
            break;
          case "showGift":
            this.showTaxes = true;
            this.showEvenMoreTaxes = true;
            this.showCategories = 5;
            break;
          case "showSS":
            this.showTaxes = true;
            this.showEvenMoreTaxes = true;
            this.showCategories = 7;
            break;
        }
      }
    },
    reset() {
      this.coloredDots = null;
    },
  },
};
</script>

<style lang="scss">
#app-2021-11-inkomstgrupper_fi {
  font-family: 'Open Sans', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  max-width: 600px;
  margin: 0 auto;


  h1 {
    line-height: 1.2;
    font-family: 'Yle', 'Open Sans', sans-serif;
  }

  label {
    display: block;
    font-size: 14px;
    font-weight: bold;
    line-height: 1.5;
    margin-bottom: 8px;
  }


  svg {
    display: block;
    width: 100%;
    height: auto;

    .dot {
      transition: fill 0.2s;
    }

    .legend {
      font: 14px 'Open Sans', Arial, sans-serif;
      font-weight: bold;
      fill: #ff6470;
      text-anchor: end;
    }
  }

  .svg-container {
    position: relative;

    svg {
      max-height: 90vh;
    }
  }

  .fyrfalt-background {
    position: sticky;
    margin-top: 50vh;
    top: 50%;
    overflow: hidden;
    z-index: -1;
    transform: translateY(-50%);
  }

  .fyrfalt-content {
    max-width: 700px;
    margin: 0 auto;
    padding-bottom: 90vh;
    margin-top: -50vh;

    .content-box {
      margin-top: 95vh;
      background: #fafafa;
      color: #000;
      opacity: 0.9;
      padding: 20px;
      line-height: 1.4;

      &.first {
        margin-top: 0;
      }

      h1,
      h2,
      h3 {
        margin-top: 0;
      }

      > p:last-child {
        margin-top: 12px;
        margin-bottom: 0;
      }

      > p:first-child {
        margin-top: 0;
      }
    }

    .factbox {
      margin-left: -20px;
      margin-right: -20px;
      background: #f1f1f1;
      padding: 20px;
    }

    .factbox-collapsed {
      width: 100%;
    }
  }

  .intro {
    width: 100%;
    position: relative;

    h1 {
      text-align: center;
      margin: 24px auto;
    }

    p {
      margin: 0.85em auto !important;

  }

    .content-box {
      max-width: 800px;
      margin: 0 auto;
      padding: 20px;
      line-height: 1.5;
    }
  }

  .axis {
    text {
      font: 12px 'Open Sans', Arial, sans-serif;
    }

    path.domain {
      fill: none;
      stroke: #000;
      shape-rendering: crispEdges;

      @media (prefers-color-scheme: dark) {
        stroke: #fff;
      }
    }

    line {
      fill: none;
      stroke: #000;
      shape-rendering: crispEdges;

      @media (prefers-color-scheme: dark) {
        stroke: #fff;
      }
    }
  }

  .end {
    line-height: 1.4;
    padding-top: 10px;

    .content-box {
      max-width: 800px;
      margin: 0 auto;
      padding: 20px;
      line-height: 1.5;
    }
  }

  .credits {
    font-size: 80%;
  }

  .taxes-svg {
    text {
      fill: #000;

      @media (prefers-color-scheme: dark) {
        fill: #fff;
      }
    }

    rect {
      transition: width 1s, height 1s;
    }
  }

  .tax-group {
    stroke: #fff;
    stroke-width: 1px;
  }
}

.yddviz-input {
  display: block;
  border: 2px solid #000;
  border-radius: 6px;
  padding: 6px;
  width: 100%;
  max-width: 400px;
  font-size: 16px;
  line-height: 1.5;
  margin-bottom: 24px;


  &:focus {
    outline: none;
    border: 2px solid #00B4C8;
    background: #f5f5f5;
  }

  &:disabled {
    border: 2px solid #ddd;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s;

  &.taxes-svg {
    position: absolute;
    top: 0;
  }
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.ydd-article {
  padding-top: 0;
}
</style>
